import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useField } from "formik";
import type { DatePickerProps } from "@mui/x-date-pickers-pro";
import DatePicker from "components/Inputs/DatePicker";

interface FormDatePickerProps extends DatePickerProps {
  name: string;
}

function FormDatePicker(props: FormDatePickerProps) {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField<string | null>(props.name);

  const hasError = Boolean(meta.touched && meta.error);
  const errorText = hasError ? t(meta.error as string) : null;

  const onChange = useCallback(
    (v: string | moment.Moment | null) => {
      helpers.setTouched(true);
      helpers.setValue(moment(v).toISOString());
    },
    [helpers]
  );

  const memoSlotProps = useMemo(
    () => ({
      ...props?.slotProps,
      field: { ...props?.slotProps?.field, onBlur: () => helpers.setTouched(true) },
      textField: {
        helperText: errorText,
        error: hasError,
        ...props?.slotProps?.textField,
      },
    }),
    [props?.slotProps, errorText, hasError, helpers]
  );

  return (
    <DatePicker
      {...props}
      name={field.name}
      value={field.value ? moment(field.value) : null}
      onChange={onChange}
      slotProps={memoSlotProps}
    />
  );
}

export default memo(FormDatePicker);
